/**
 * This `windowTitleBar` module should contain only reducers that respond to events received directly from the window title bar.
 * Other reducers (such as responding to docking, FEA, workspaces or window management) should go in the `main` module.
 */
/**
 * Notes. Because reducers use Immer, you can't just console.log pieces of your store. You'll get a bunch of proxy
 * stuff from the WritableDraft<> template. Wrap your data in unImmer() to print it out normally.
 */

import uuidv4 from "uuid-random";
import { ActionReducerMapBuilder, CMD, unImmer } from "../../../../common/redux/createReducer";
import { RouterClient } from "../../../../clients/routerClient";
import { actions } from "./actions";
import { WindowServiceStore, WindowTitleBarStore, BumpConfig, WindowDescriptor, TabCloseStatus } from "../types";
import {
	setTabDisplayTitle,
	sendCommandToWindowService,
	getTabCollection,
	updateGroupsAndWorkspace,
	removeFromTabCollection,
	getPermission,
	findTabCollectionId,
	promoteWindow,
	showThenHide,
	updateGroupStatusForAllTitleBars,
} from "./main";
import { System } from "../../../../common/system";
import { Rectangle, WindowState } from "../../../../FEA";
import { Logger } from "../../../../clients/logger";
import { AppsClient } from "../../../../clients";

const getInitialState = (): WindowTitleBarStore => {
	return {
		windows: {},
		dragging: null,
		tabCollections: {},
		useDOMBasedMovement: true,
		closingTabs: null,
	};
};

/**
 * Starts the docking system's tiling tracking.
 */
const startTilingOrTabbing = (windowName: string) => {
	RouterClient.transmit("DockingService.startTilingOrTabbing", {
		windowIdentifier: {
			windowName,
		},
	});
};

/**
 * Stops the docking system's tiling tracking but without taking any action (such as moving or
 * tiling the window)
 */
const cancelTilingOrTabbing = () => {
	RouterClient.transmit("DockingService.cancelTilingOrTabbing", {});
};

/**
 * Stops the docking system's tiling tracking. The window that was being dragged will
 * be relocated to the last mouse position if it isn't set to be tiled.
 */
const stopTilingOrTabbing = async () => {
	// No need to wait for a response
	await RouterClient.query("DockingService.stopTilingOrTabbing", {});
};

/**
 * Save the edited title in the window's workspace data.
 *
 * Legacy implementations stored the title in a window's "options" record, but this results in a very convoluted path
 * through the window service's window abstractions.
 */
export const setUserDefinedTitle = (windowName: string, title: string | null) => {
	sendCommandToWindowService(windowName, "setComponentState", { field: "persistedTitle", value: title });
};

/**
 * Sends a command to the docking system to form a group. The store
 * will not be updated immediately. The results of the action will be set by dockingMain with calls
 * to updateGroupData().
 * - Note that `formGroup` is a transmit while `leaveGroup` is a query!
 * - Note that `formGroup` uses windowName while `leaveGroup` uses name!
 * @param windowName The name of the window
 */
const formGroup = async (windowName: string) => {
	const channelName = `DockingService.formGroup`;
	await RouterClient.transmit(channelName, {
		windowName,
	});
};

/**
 * Sends a command to the docking system to leave a group. See comment in formGroup() above.
 * @param windowName The name of the window
 */
const leaveGroup = async (windowName: string) => {
	const channelName = `DockingService.leaveGroup`;
	await RouterClient.query(channelName, {
		name: windowName,
	});
};

/**
 * Relocates a tab within its tab collection
 */
export const changePositionInTabCollection = ({
	state,
	windowName,
	position,
}: {
	state: WindowServiceStore;
	windowName: string;
	position: number;
}) => {
	let tabCollection = getTabCollection(state, windowName);
	// If a single window's tab is dragged and dropped within its own titlebar then there won't be a tab collection and thus nothing to do
	if (!tabCollection) return;
	tabCollection.tabs = tabCollection.tabs.filter((tab) => tab !== windowName);
	tabCollection.tabs.splice(position, 0, windowName);
};

/**
 * Normalize a window's state after it joins a tab collection so it takes on the same physical state as the destination window.
 */
export const normalizeWindowStateAfterTabbing = ({
	state,
	windowName,
	destinationWindowName,
	tabCollectionId,
}: {
	state: WindowServiceStore;
	windowName: string;
	destinationWindowName: string;
	tabCollectionId: string;
}) => {
	// Normalize the window state
	if (state.windowTitleBar.windows[windowName]) {
		state.windowTitleBar.windows[windowName].tabCollectionId = tabCollectionId;
		// Ensure the window picks up the tab collection's maximized state. This is possible when dragging a window from one
		// monitor into a maximized collection on another monitor. We must set the dragged window into emulated maximization.
		state.windowTitleBar.windows[windowName].maximized = state.windowTitleBar.windows[destinationWindowName]?.maximized;
		if (state.main.maximization[destinationWindowName]) {
			// New emulated normal bounds is either the promoted tab's emulated (it emulated maximization) or real (if OS maximization) normal bounds
			state.main.maximization[windowName] = {
				...state.main.maximization[destinationWindowName],
				emulated: true,
			};
		} else {
			// If not maximized then make sure it doesn't have emulated maximization.
			delete state.main.maximization[windowName];
		}
	} else {
		Logger.system.warn(`invariant: addTabToCollection can't find window entry for ${windowName}`);
	}
};

/**
 * Adds a tab to a collection or creates a new collection. If dropIntoWindow is undefined then a new tab collection will be created.
 */
export const addToTabCollection = ({
	state,
	windowName,
	position,
	destinationWindowName,
	cmd,
}: {
	state: WindowServiceStore;
	windowName: string;
	position: number;
	destinationWindowName: string;
	cmd: CMD;
}) => {
	// Remove the tab from its current collection
	removeFromTabCollection({ state, windowName, cmd });

	// Get the tab collection for the remote window (where we're placing the new tab)
	let destinationTabCollection = getTabCollection(state, destinationWindowName);
	const tabCollectionId = destinationTabCollection?.tabCollectionId ?? `StackedWindow-${uuidv4()}`;

	// If the destination window is not part of a collection then create a new collection with the destination window as the initial tab
	if (!destinationTabCollection) {
		destinationTabCollection = state.windowTitleBar.tabCollections[tabCollectionId] = {
			tabCollectionId,
			tabs: [destinationWindowName],
			promotedWindowName: destinationWindowName,
			scroll: 0,
		};
		if (state.windowTitleBar.windows[destinationWindowName]) {
			state.windowTitleBar.windows[destinationWindowName].tabCollectionId = tabCollectionId;
		}
	}

	// Add the dragged tab into the destination collection at the desired position (don't remove any tabs from the destination!)
	destinationTabCollection.tabs.splice(position, 0, windowName);

	// Normalize the window state

	normalizeWindowStateAfterTabbing({ state, windowName, destinationWindowName, tabCollectionId });
};

/**
 * Effects to promote a window into a new tab collection.
 */
const restoreThenMoveThenShow = async (windowName: string, bounds: Rectangle | null, windowState: WindowState) => {
	// If the window that was dragged had been maximized then restore it before resizing it
	if (windowState === "maximized") {
		await sendCommandToWindowService(windowName, "restore");
	}
	// Resize the dragged window to the size of the destination window
	if (bounds) {
		await sendCommandToWindowService(windowName, "setBounds", {
			bounds: {
				top: bounds.y,
				left: bounds.x,
				width: bounds.width,
				height: bounds.height,
			},
		});
	}
	// Always show the dragged window since it will now be promoted
	await sendCommandToWindowService(windowName, "show");
	// Always bring it to front otherwise it could remain occluded
	await sendCommandToWindowService(windowName, "bringToFront");
};

/**
 * This runs the effects necessary to physically effect the addToTabCollection action.
 */
const runAddToTabCollectionEffects = async ({
	state,
	windowName,
	destinationWindowName,
	cmd,
}: {
	state: WindowServiceStore;
	windowName: string;
	destinationWindowName: string;
	cmd: CMD;
}) => {
	const destinationWindow = state.main.windows[destinationWindowName];
	const sourceWindow = state.main.windows[windowName];
	const bounds = unImmer(destinationWindow?.physicalWindowState.bounds) ?? null;

	cmd.run(restoreThenMoveThenShow, {
		args: [windowName, bounds, sourceWindow?.physicalWindowState?.windowState],
	});
	if (destinationWindow) {
		// Always hide the destination window because it will no longer be promoted
		cmd.run(sendCommandToWindowService, {
			args: [destinationWindowName, "hide"],
		});
	}
};

/**
 * This is called as the effect for a keyboard navigation of tabs.
 *
 * Shows the window and then focuses that window's title bar. Note that focus does not work
 * until after the window has been shown, so we must await completion of the show effect.
 * Also, by default, focus() will focus an app's contents (inside a browser view if one exists)
 * but in this case we want focus to remain inside the titlebar so that the end user can
 * click ctrl-tab multiple times to cycle through the tabs.
 *
 * The previous window is hidden after the new window is shown, to prevent white flashes
 */
const showThenFocusAndHide = async (showWindow: string, hideWindow: string) => {
	await showThenHide(showWindow, hideWindow);
	const finWindow = System.Window.wrap(showWindow);
	if (finWindow) {
		// For Electron windows, always focus the title bar
		finWindow.focusTitleBarWindow();
	} else {
		// For any other window just a normal focus will do
		await sendCommandToWindowService(showWindow, "focus");
	}
};

/**
 * Sets the new promoted tab if it doesn't match with current active tab
 */
export const promoteTabWindow = (state: WindowServiceStore, windowName: string, cmd: CMD) => {
	const collection = getTabCollection(state, windowName);
	if (collection && collection.promotedWindowName !== windowName) {
		cmd.run(showThenFocusAndHide, {
			args: [windowName, collection.promotedWindowName],
		});
		promoteWindow(state, windowName);
		updateGroupsAndWorkspace(state, cmd);
	}
};

/**
 * When a dragging operation ends without a tabbing event, control is ceded to the window service which
 * will either tile the window or move it to the desktop. But first, if the window is maximized then
 * we unmaximize it, and if it was emulated maximization then it is resized restored back to the saved
 * normal bounds. Then we let the window service do its thing by sending it a stopTilingOrTabbing message.
 * Finally we show the window, because it might have been dragged as a hidden tab.
 */
const cedeControlToWindowService = async (
	windowName: string,
	normalBounds: Rectangle | null,
	windowState: WindowState
) => {
	if (windowState === "maximized") {
		await sendCommandToWindowService(windowName, "restore");
	}
	if (normalBounds)
		await sendCommandToWindowService(windowName, "setBounds", {
			bounds: {
				...normalBounds,
				top: normalBounds.y,
				left: normalBounds.x,
			},
		});
	await stopTilingOrTabbing();
	sendCommandToWindowService(windowName, "show");
};

const bumpConfigFromDescriptor = (descriptor?: WindowDescriptor): BumpConfig | null => {
	const header = descriptor?.customData?.foreign?.components?.["Window Manager"]?.FSBLHeader;
	const adjustContentHeights = header?.adjustContentHeight ?? true;
	const bumpFixedElements = header?.bumpElements ?? true;
	const hackScrollbar = header?.hackScrollbar ?? true;

	// By default, fixed positioned elements with a top style set to '0px' will be bumped by the header height and the
	// DOM will be monitored for nodes that are added dynamically and bump them if they are fixed with a top style of 0px.
	return {
		adjustContentHeights,
		hackScrollbar,
		bumpFixedElements,
		monitorFixedAdditions: bumpFixedElements !== false,
		fixed: "0Positioned",
		bumpBy: "25px",
		...bumpFixedElements,
	};
};

const addReducerCases = (builder: ActionReducerMapBuilder<WindowServiceStore>) => {
	builder
		/**
		 * Called when the close button is pressed. This will trigger effects to
		 * close the window, and if it is part of a tab collection, to close all
		 * the other windows in that collection.
		 */
		.addCase(actions.close, (state, action, cmd) => {
			const { windowName } = action["@@remote"];

			// Close all the tabs in the collection, or just the window if not in a collection
			const tabCollection = getTabCollection(state, windowName);
			const tabs = tabCollection?.tabs ?? [windowName];
			if (tabCollection) {
				// initialize the initial closing status for all tabs
				const data = Object.fromEntries(tabs.map((tab) => [tab, TabCloseStatus.closingStarted]));
				if (!state.windowTitleBar.closingTabs) {
					state.windowTitleBar.closingTabs = {};
				}
				state.windowTitleBar.closingTabs[tabCollection.tabCollectionId] = data;
			}

			tabs.forEach((tab) => {
				cmd?.run(sendCommandToWindowService, {
					args: [tab, "close", { removeFromWorkspace: true }],
				});
			});
		})
		/**
		 * Called when a window wrapper executed the `close-requested` event.
		 * The function updates the closing status of a single window in the tab collection.
		 */
		.addCase(actions.setClosingTabStatus, (state, action) => {
			const { tabCollectionId, windowName, status } = action.payload;

			if (state.windowTitleBar.closingTabs?.[tabCollectionId]?.[windowName] !== undefined) {
				state.windowTitleBar.closingTabs[tabCollectionId][windowName] = status;
			}
		})
		/**
		 * Called when a window wrapper finished waiting for closing status of  all windows in tab collection.
		 */
		.addCase(actions.removeClosingTabs, (state, action) => {
			const tabCollectionId = action.payload;
			const windows = state.windowTitleBar.closingTabs?.[tabCollectionId];

			// removes unnecessary data when all windows in collection has the same status closingAborted or closingCompleted
			if (
				windows &&
				(Object.values(windows).every((status) => status === TabCloseStatus.closingAborted) ||
					Object.values(windows).every((status) => status === TabCloseStatus.closingCompleted))
			) {
				delete state.windowTitleBar.closingTabs?.[tabCollectionId];
			}
		})
		/**
		 * Called when the clone button is pressed. This will trigger effects to
		 * clone the window.
		 */
		.addCase(actions.clone, (state, action, cmd) => {
			const { windowName } = action["@@remote"];

			cmd?.run(AppsClient.clone, {
				args: [{ windowName }],
			});
		})
		/**
		 * Closes a tab. We proactively remove the window from the tab collection in order to
		 * prevent the entire tab collection from being closed with the `closed` event comes in
		 * from FEA. (If a `closed` event arrives for the currently promoted tab then it is assumed
		 * that the user closed the window from the operating system, intending to close the entire
		 * collection).
		 */
		.addCase(actions.closeTab, (state, action, cmd) => {
			const { targetWindowName } = action.payload;
			const windowState = state.windowTitleBar.windows[targetWindowName];
			if (windowState) {
				windowState.toBeEjected = true;
			}
			cmd?.run(sendCommandToWindowService, {
				args: [targetWindowName, "close", { removeFromWorkspace: true }],
			});
		})
		/**
		 * Maximization is complicated in tab groups. When a tab group is maximized, the promoted tab at the time is
		 * maximized at the operating system level. However if another tab is promoted while the collection is maximized
		 * then that tab is merely resized to the maximized size. We call this "emulated maximization" and it is done
		 * to prevent the operating system's maximize animation effect that breaks the illusion of tabs. All windows in
		 * the tab collection will display the restore icon when maximized, whether they are truly maximized or only
		 * emulated.
		 *
		 * When the restore icon is clicked on a window (unmaximize) then the window will resized to previously saved
		 * normal bounds if they exist (meaning emulated maximization) or otherwise unmaximized (meaning true OS maximization).
		 * Either way, the accompanying resize event will be received in the main module causing all hidden tabs to also
		 * return to normal bounds size.
		 */
		.addCase(actions.setMaximize, (state, action, cmd) => {
			const { windowName } = action["@@remote"];
			if (action.payload.toggleState) {
				// If maximizing then just maximize.
				cmd?.run(sendCommandToWindowService, {
					args: [windowName, "maximize"],
				});
			} else {
				const maximization = state.main.maximization[windowName];

				// If the window is truly maximized then just restore it. This will also update the windowState
				// to the correct state
				// In MacOS there isn't a "restore" only resizes therefore, for Mac this will
				// only update the window state - the resize will happen in the setBounds below
				cmd?.run(sendCommandToWindowService, {
					args: [windowName, "restore"],
				});

				// If the window is emulated maximized (MacOS) then just restore it to its previous saved normal bounds.
				if (maximization?.emulated) {
					cmd?.run(sendCommandToWindowService, {
						args: [
							windowName,
							"setBounds",
							{
								bounds: {
									left: maximization.normalBounds.x,
									top: maximization.normalBounds.y,
									width: maximization.normalBounds.width,
									height: maximization.normalBounds.height,
								},
							},
						],
					});
				}
			}
		})
		.addCase(actions.minimize, (state, action, cmd) => {
			const { windowName } = action["@@remote"];
			cmd?.run(sendCommandToWindowService, {
				args: [windowName, "minimize"],
			});
		})
		/**
		 * Promotes a tab, hiding the previously promoted tab and showing the newly promoted tab.
		 */
		.addCase(actions.promoteTab, (state, action, cmd) => {
			const { targetWindowName } = action.payload;
			promoteTabWindow(state, targetWindowName, cmd);
		})
		.addCase(actions.setAlwaysOnTop, (state, action, cmd) => {
			const { windowName } = action["@@remote"];
			cmd?.run(sendCommandToWindowService, {
				args: [
					windowName,
					"setAlwaysOnTop",
					{
						alwaysOnTop: action.payload.toggleState,
					},
				],
			});
		})
		/**
		 * Set by the end user when they toggle the group/eject button
		 */
		.addCase(actions.setGrouped, (state, action, cmd) => {
			const { windowName } = action["@@remote"];

			if (action.payload.toggleState) {
				cmd?.run(formGroup, {
					args: [windowName],
				});
			} else {
				cmd?.run(leaveGroup, {
					args: [windowName],
				});
			}
		})
		/**
		 * Set when the user edits the title for a tab. This will cause the title to update
		 * and also trigger an effect to save that edited title in the tabbed window's component state.
		 */
		.addCase(actions.setUserDefinedTitle, (state, action, cmd) => {
			const { windowName } = action["@@remote"];
			const { title } = action.payload;
			if (state.main.windows[windowName]) {
				state.main.windows[windowName].titles.titleFromUser = title;
				setTabDisplayTitle(state, windowName, cmd);
			}

			setUserDefinedTitle(windowName, title);
		})
		/**
		 * This gets called continually during a tab dragging operation. The status will always
		 * reflect the new desired state if the user were to drop the tab at that moment. The new
		 * desired state might be to move the tab to another collection or remove it from the collection,
		 * such as to drop on the desktop or to tile.
		 */
		.addCase(actions.setDraggingStatus, (state, action, cmd) => {
			if (!state.windowTitleBar.dragging) {
				const windowName = action.payload.windowNameBeingDragged;
				cmd?.run(startTilingOrTabbing, { args: [windowName] });
			}
			state.windowTitleBar.dragging = action.payload;
		})
		/**
		 * This gets called when the user stops a dragging operation (e.g. by releasing the mouse button).
		 * The state's draggingStatus should reflect the most recent position of the mouse. This reducer
		 * will then reorganize tab collections to reflect that status.
		 */
		.addCase(actions.stopDragging, (state, action, cmd) => {
			const { dragging } = state.windowTitleBar;
			if (dragging) {
				const windowName = dragging.windowNameBeingDragged;
				// If we dragged into a window at a valid position then move the tab into that collection
				// Note that it's possible to drag the active window's tab to a different location in the same window
				if (dragging.inWindowName && dragging.temporaryPosition !== null) {
					const sourceTabCollection = getTabCollection(state, windowName);
					const destinationTabCollection = getTabCollection(state, dragging.inWindowName);

					if (windowName === dragging.inWindowName) {
						// This only happens when relocating the promoted tab
						changePositionInTabCollection({
							state,
							windowName,
							position: dragging.temporaryPosition,
						});
					} else if (destinationTabCollection && sourceTabCollection === destinationTabCollection) {
						// Relocation of a non-promoted tab within the same tab collection
						changePositionInTabCollection({
							state,
							windowName,
							position: dragging.temporaryPosition,
						});
						cmd.run(showThenHide, {
							args: [windowName, destinationTabCollection.promotedWindowName],
						});
					} else {
						addToTabCollection({
							state,
							windowName,
							position: dragging.temporaryPosition,
							destinationWindowName: dragging.inWindowName,
							cmd,
						});
						runAddToTabCollectionEffects({
							state,
							windowName,
							destinationWindowName: dragging.inWindowName,
							cmd,
						});
					}
					cmd?.run(cancelTilingOrTabbing);
				} else {
					const normalBounds = state.main.maximization[windowName]?.normalBounds;
					if (state.main.dockingOperation.type !== "swapping") {
						// Remove it from its current collection unless we're swapping
						removeFromTabCollection({ state, windowName, cmd });
						if (state.windowTitleBar.windows[windowName]) state.windowTitleBar.windows[windowName].maximized = false;
						delete state.main.maximization[windowName];
					}

					cmd.run(cedeControlToWindowService, {
						args: [
							windowName,
							normalBounds ? unImmer(normalBounds) : null,
							state.main.windows[windowName]?.physicalWindowState.windowState,
						],
					});
				}
				// Once all the collections are updated we always promote the window that was dragged (regardless of where it lands)
				promoteWindow(state, windowName);
			}
			state.windowTitleBar.dragging = null;
			updateGroupsAndWorkspace(state, cmd);
		})
		// Called by window service if finsemble-electron-adapter.useDOMBasedMovement is set to false
		.addCase(actions.disableDOMBasedMovement, (state) => {
			state.windowTitleBar.useDOMBasedMovement = false;
		})
		/**
		 * Called from the windowTitleBar UI component. We currently have no other reliable way to know which windows to configure
		 * with title bars (spawned apps with FSBLHeader and some window.open calls).
		 */
		.addCase(actions.register, (state, action, cmd) => {
			const windowName = action.payload;
			const windowDescriptor = state.main.launchData[windowName]?.windowDescriptor ?? null;

			// Opened by window.open if windowDotOpen is true, or if the descriptor is missing (if registration occurs before launchData is available)
			const isWindowDotOpen = windowDescriptor ? state.main.launchData[windowName].windowDotOpen : true;
			const foreignServices = windowDescriptor?.customData?.foreign?.services;
			const { servicesConfig } = state.finsembleConfig;

			// All the various places where tabbing can be enabled/disabled. Defaults to true (enabled).
			const disallowTabbingConfig =
				foreignServices?.windowService?.ignoreTilingAndTabbingRequests ??
				foreignServices?.dockingService?.ignoreTilingAndTabbingRequests;

			const globalTabbingConfig = servicesConfig?.window?.tabbing?.enabled ?? servicesConfig?.docking?.tabbing?.enabled;

			const enableTabbingConfig =
				// If tabbing is disabled globally then ignore single component configs
				globalTabbingConfig === false
					? false
					: (foreignServices?.windowService?.allowTabbing ??
						foreignServices?.dockingService?.allowTabbing ??
						globalTabbingConfig);

			// Tabbing is always disabled if the app was opened with window.open()
			let enableTabbing: boolean =
				(disallowTabbingConfig !== undefined ? !disallowTabbingConfig : (enableTabbingConfig ?? true)) &&
				!isWindowDotOpen;

			// workspaceWindow is true when the window is in the workspace, regardless of what `addToWorkspace` is; only windows which are in the workspace can be tabbed
			if (!state.main.launchData[windowName]?.workspaceWindow) {
				enableTabbing = false;
			}
			state.windowTitleBar.windows[windowName] = {
				title: null,
				editable:
					servicesConfig?.window?.allowWindowTitleRenaming ?? servicesConfig?.docking?.tabbing?.allowEditing ?? true,
				groupable: false,
				isGrouped: false,
				// A window is just now being added, so it probably is not in a tab collection yet, but there's a chance it was
				// already added to a tab collection via a workspace load or some other act that occurred prior to receiving the
				// "opened" event from FEA
				tabCollectionId: findTabCollectionId(state, windowName) ?? null,
				enableTabbing,
				showMaximizeButton: getPermission(state, windowName, "maximize"),
				showMinimizeButton: getPermission(state, windowName, "minimize"),
				showAlwaysOnTopButton: getPermission(state, windowName, "alwaysOnTop"),
				showCloneButton: getPermission(state, windowName, "clone"),
				showCloseButton: getPermission(state, windowName, "close"),
				showShareButton: false, // turned on by DND listener
				bumpConfig: bumpConfigFromDescriptor(windowDescriptor),
				icon: windowDescriptor?.icons?.titleBarIcon ?? null,
				alwaysOnTop: state.main.windows[windowName]?.physicalWindowState.isAlwaysOnTop ?? false,
				// Don't trust Electron's maximized window state. It will be set to maximized even if the window is merely resized to the full monitor dimensions.
				// Instead, we keep state.main.maximization up to date so that it is reliable
				maximized: !!state.main.maximization[windowName],
				toBeEjected: false,
			};

			setTabDisplayTitle(state, windowName, cmd);
			updateGroupStatusForAllTitleBars(state);
		})
		// Called when the user scrolls the tab collection, in order to synchronize the scroll position of the hidden windows
		.addCase(actions.scroll, (state, action) => {
			const { windowName } = action["@@remote"];
			let collection = getTabCollection(state, windowName);
			if (!collection) return;
			collection.scroll = action.payload.scroll;
		})
		// Shifts the promoted tab to the left or right (from end user keyboard navigation)
		.addCase(actions.shiftPromotedTab, (state, action, cmd) => {
			const { windowName } = action["@@remote"];
			const collection = getTabCollection(state, windowName);
			if (!collection) return;
			const position = collection.tabs.findIndex((tab) => tab === windowName);
			let newPosition = position + action.payload;
			if (newPosition >= collection.tabs.length) newPosition = 0;
			if (newPosition < 0) newPosition = collection.tabs.length - 1;
			collection.promotedWindowName = collection.tabs[newPosition];
			cmd.run(showThenFocusAndHide, {
				args: [collection.promotedWindowName, windowName],
			});
			updateGroupsAndWorkspace(state, cmd);
		});
};

export default {
	getInitialState,
	addReducerCases,
	actions,
};
